'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  title: string;
  icon: string;
  description: string;
  capabilities: string[];
  examples: string[];
}

const ServicesShowcase = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
    {
      id: 'epc',
      title: 'EPC Services',
      icon: 'CogIcon',
      description: 'Comprehensive Engineering, Procurement, and Construction solutions for electrical and mechanical systems',
      capabilities: [
        'Complete electrical system design and installation',
        'Mechanical engineering and HVAC solutions',
        'Power distribution and substation construction',
        'Industrial automation and control systems',
        'Project management from concept to commissioning'
      ],
      examples: [
        'Amahoro Stadium - Complete electrical infrastructure',
        'Bugesera Airport - Power distribution systems',
        'Industrial Parks - Comprehensive EPC delivery'
      ]
    },
    {
      id: 'workshop',
      title: 'Workshop & Assembly',
      icon: 'WrenchScrewdriverIcon',
      description: 'State-of-the-art manufacturing and assembly facilities for custom electrical solutions',
      capabilities: [
        'Custom switchboard and panel manufacturing',
        'Cable tray and support system fabrication',
        'Transformer and generator assembly',
        'Quality testing and certification',
        'On-site installation and commissioning'
      ],
      examples: [
        'KEMRI Labs - Custom laboratory power systems',
        'Hospital facilities - Medical-grade electrical panels',
        'Data centers - Precision cooling and power solutions'
      ]
    },
    {
      id: 'retail',
      title: 'Retail & Dealership',
      icon: 'ShoppingBagIcon',
      description: 'Authorized distribution of premium electrical and mechanical equipment from world-leading manufacturers',
      capabilities: [
        'Schneider Electric authorized distributor',
        'Toshiba and Fuji Electric partnerships',
        'MEP (Mechanical, Electrical, Plumbing) material supply',
        'Genuine spare parts and components',
        'Technical support and training',
        'Warranty and after-sales service'
      ],
      examples: [
        'Government procurement - Bulk equipment supply',
        'Industrial clients - Specialized component sourcing',
        'Maintenance contracts - Ongoing parts supply'
      ]
    }
  ];

  const toggleService = (serviceId: string) => {
    if (!isHydrated) return;
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const toggleServiceSelection = (serviceId: string) => {
    if (!isHydrated) return;
    setSelectedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  return (
    <section id="services" className="py-20 bg-card">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Comprehensive EPC Solutions
            </h2>
            <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
              From design to commissioning, we deliver end-to-end engineering excellence across all project phases
            </p>
          </div>

          <div className="space-y-6 mb-12">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-surface rounded-lg shadow-card overflow-hidden transition-all duration-250"
              >
                <button
                  onClick={() => toggleService(service.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-muted transition-colors duration-250"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <Icon name={service.icon as any} size={24} variant="solid" className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-headline text-2xl font-bold text-primary mb-1">{service.title}</h3>
                      <p className="font-body text-muted-foreground">{service.description}</p>
                    </div>
                  </div>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    variant="outline"
                    className={`text-primary transition-transform duration-250 flex-shrink-0 ${
                      isHydrated && expandedService === service.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isHydrated && expandedService === service.id && (
                  <div className="px-6 pb-6 border-t border-border">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6">
                      <div>
                        <h4 className="font-headline text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                          <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
                          Key Capabilities
                        </h4>
                        <ul className="space-y-3">
                          {service.capabilities.map((capability, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <Icon name="ArrowRightIcon" size={16} variant="outline" className="text-accent mt-1 flex-shrink-0" />
                              <span className="font-body text-muted-foreground">{capability}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-headline text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                          <Icon name="BriefcaseIcon" size={20} variant="solid" className="text-accent" />
                          Project Examples
                        </h4>
                        <ul className="space-y-3">
                          {service.examples.map((example, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <Icon name="BuildingOfficeIcon" size={16} variant="outline" className="text-primary mt-1 flex-shrink-0" />
                              <span className="font-body text-muted-foreground">{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-border">
                      <button
                        onClick={() => toggleServiceSelection(service.id)}
                        className={`w-full sm:w-auto px-6 py-3 rounded-lg font-cta font-semibold transition-all duration-250 flex items-center justify-center gap-2 ${
                          selectedServices.includes(service.id)
                            ? 'bg-success text-white' :'bg-primary text-white hover:bg-secondary'
                        }`}
                      >
                        <Icon
                          name={selectedServices.includes(service.id) ? 'CheckIcon' : 'PlusIcon'}
                          size={20}
                          variant="solid"
                        />
                        {selectedServices.includes(service.id) ? 'Service Selected' : 'Select for Quote'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {isHydrated && selectedServices.length > 0 && (
            <div className="bg-accent/10 border-2 border-accent rounded-lg p-6 text-center">
              <p className="font-body text-lg text-foreground mb-4">
                You've selected <span className="font-bold">{selectedServices.length}</span> service{selectedServices.length > 1 ? 's' : ''} for your project quote
              </p>
              <button className="bg-accent hover:bg-accent/90 text-white font-cta font-bold px-8 py-3 rounded-lg transition-all duration-250 transform hover:scale-105">
                Continue to Quote Request
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcase;